import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Colors, Dimensions, Spacing } from '@/shared/theme';
import { Text, Column } from '@/shared/ui';
import logo from '@/assets/images/happy-hearts-logo.svg';
import downArrow from '@/assets/images/downArrow.svg';
import { SupportModal } from './SupportModal';
import { ProgressBar } from './ProgressBar';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useDeviceDimensions } from '@/hooks';
import { NEED_HELP, CHANGE_LANGUAGE, LANGUAGE_CHANGED, EVENT_SOURCES } from '@/consts/events';
import { convertHexToRgba } from '@/utils/colors';
import { HAPPY_HEARTS_SITE_URL } from '@/components/happyHeartsSite';

const shadowColor = convertHexToRgba(Colors.foreground, 0.08);

const HeaderContainer = styled.header<{ $showShadow: boolean }>`
  width: 100%;
  overflow: visible;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  position: sticky;
  top: 0;
  background-color: rgba(251, 247, 240, 0.94);
  padding: 0;
  border-bottom: 1px solid rgba(35, 32, 28, 0.1);
  backdrop-filter: saturate(1.4) blur(8px);
  -webkit-backdrop-filter: saturate(1.4) blur(8px);
  box-shadow: ${(props) => (props.$showShadow ? `0px 0px 24px ${shadowColor}` : 'none')};
  transition: box-shadow 0.15s ease-in-out;
  z-index: 100;
`;

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1240px;
  min-height: 64px;
  padding: 9px 4vw;
  box-sizing: border-box;
`;

const LeftSide = styled.div`
  display: flex;
  align-items: center;
  justify-self: start;
  width: ${Dimensions.c2}px;
`;

const CenterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${Dimensions.c8}px;
  position: relative;
  border-radius: 100px;
`;

const Logo = styled.img`
  width: auto;
  height: 30px;
`;

const LogoLink = styled.a`
  display: inline-flex;
  border-radius: 4px;
`;

const RightSide = styled.div`
  display: flex;
  align-items: center;
  justify-self: end;
  justify-content: flex-end;
  width: ${Dimensions.c2}px;
`;

const LanguageSwitcherButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.x0_5}px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: ${Spacing.x1}px;
`;

const LanguageDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${Colors.background};
  border: 1px solid ${Colors.foregroundDisabledAlt};
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  z-index: 200;
  min-width: 120px;
`;

const LanguageOption = styled.button<{ $isSelected: boolean }>`
  width: 100%;
  padding: ${Spacing.x1_5}px ${Spacing.x2}px;
  background: ${({ $isSelected }) => ($isSelected ? Colors.backgroundAlt : 'transparent')};
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  font-size: 14px;

  &:hover {
    background: ${Colors.backgroundAlt};
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const LanguageSwitcherContainer = styled.div`
  position: relative;
`;

// Keep the help action visually aligned with the public site's compact menu control.
const HelpButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  padding: 0 12px;
  border: 1.5px solid rgba(35, 32, 28, 0.2);
  border-radius: 10px;
  background: transparent;
  box-shadow: none;
  color: #23201c;
  font-family: inherit;
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.12s ease;

  &:hover {
    background: rgba(35, 32, 28, 0.06);
  }
  &:active {
    transform: scale(0.96);
  }
  & svg {
    width: 24px;
    height: 24px;
  }
`;

type HeaderProps = {
  showDivider?: boolean;
  progress?: number;
  currentStep?: number;
  totalSteps?: number;
  showLogo?: boolean;
  /**
   * When the desktop phase rail is showing (RegistrationPhaseRail), suppress the
   * desktop top step bar so the two don't show conflicting progress (the rail is
   * 4 phases, this bar is per-step). Mobile has no rail, so it keeps this bar.
   */
  hasPhaseRail?: boolean;
};

const Header: React.FC<HeaderProps> = ({
  showDivider = false,
  progress = 0,
  currentStep = 0,
  totalSteps = 0,
  showLogo = true,
  hasPhaseRail = false,
}) => {
  const { t, i18n } = useTranslation();
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [showShadow, setShowShadow] = useState(false);
  const languageRef = useRef<HTMLDivElement>(null);
  const { track } = useAnalytics();
  const { isMobileDimensions, isDesktopDimensions } = useDeviceDimensions();

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
  ];

  const handleLanguageToggle = () => {
    track({ event: CHANGE_LANGUAGE });
    setIsLanguageOpen(!isLanguageOpen);
  };

  const handleLanguageChange = (code: string) => {
    track({
      event: LANGUAGE_CHANGED,
      properties: { language: code, previous_language: i18n.language },
    });
    i18n.changeLanguage(code);
    setIsLanguageOpen(false);
  };

  const handleHelpClick = () => {
    track({ event: NEED_HELP, properties: { source: EVENT_SOURCES.HEADER } });
    setIsSupportOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageRef.current && !languageRef.current.contains(event.target as Node)) {
        setIsLanguageOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsLanguageOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowShadow(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Listen for custom event to open support modal from other components
  useEffect(() => {
    const handleOpenSupport = () => {
      setIsSupportOpen(true);
    };
    window.addEventListener('openSupportModal', handleOpenSupport);
    return () => {
      window.removeEventListener('openSupportModal', handleOpenSupport);
    };
  }, []);

  const shouldShowProgress = showDivider && progress > 0 && totalSteps > 0;

  return (
    <HeaderContainer $showShadow={showShadow}>
      <Container>
        <LeftSide>
          {showLogo && (
            <LogoLink href={HAPPY_HEARTS_SITE_URL} aria-label="Happy Hearts home">
              <Logo src={logo} alt="Happy Hearts Logo" />
            </LogoLink>
          )}
        </LeftSide>
        <CenterContainer>
          {shouldShowProgress && isDesktopDimensions && !hasPhaseRail && (
            <ProgressBar progress={progress} current={currentStep} total={totalSteps} />
          )}
        </CenterContainer>
        <RightSide>
          {/* Language switcher hidden per RND-31080 - keep code for future re-enable */}
          {false && (
            <LanguageSwitcherContainer ref={languageRef}>
              <LanguageSwitcherButton
                onClick={handleLanguageToggle}
                aria-label="Change Language"
                aria-expanded={isLanguageOpen}
              >
                <Text size="p3">{i18n.language.toUpperCase()}</Text>
                <img
                  src={downArrow}
                  alt=""
                  style={{ transform: isLanguageOpen ? 'rotate(180deg)' : 'none' }}
                />
              </LanguageSwitcherButton>
              <LanguageDropdown $isOpen={isLanguageOpen}>
                {languages.map((lang) => (
                  <LanguageOption
                    key={lang.code}
                    $isSelected={i18n.language === lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                  >
                    {lang.name}
                  </LanguageOption>
                ))}
              </LanguageDropdown>
            </LanguageSwitcherContainer>
          )}
          <HelpButton onClick={handleHelpClick} aria-label={t('general.need-help')}>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
              <path
                d="M9.7 9.4a2.3 2.3 0 1 1 3 2.2c-.8.3-1.2.8-1.2 1.6v.3"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
              <circle cx="12" cy="16.6" r="0.95" fill="currentColor" />
            </svg>
            {t('general.need-help')}
          </HelpButton>
        </RightSide>
      </Container>
      {/* On flow steps (hasPhaseRail) mobile uses Boris's continuous phase bar,
          rendered at the top of the main column by Layout; suppress this legacy
          segmented bar there so the two don't both show. Non-phased routes that
          still report progress keep this bar. */}
      {shouldShowProgress && isMobileDimensions && !hasPhaseRail && (
        <Column>
          <ProgressBar progress={progress} current={currentStep} total={totalSteps} />
        </Column>
      )}
      <SupportModal isOpen={isSupportOpen} onClose={() => setIsSupportOpen(false)} />
    </HeaderContainer>
  );
};

export { Header };
